import { useMemo, useRef, useState, type ReactElement } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileUp, Upload } from "lucide-react";
import { Badge, Button, Stack, Surface, Text } from "@ux/primitives";
import { DataTable, Toolbar, type DataColumn } from "@ux/compositions";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { AvailableOfficesPanel } from "../../sage-offices/AvailableOfficesPanel";
import { useSageOffices } from "../../sage-offices/hooks";
import { AvailableWorkspacesPanel } from "../../workspaces/AvailableWorkspacesPanel";
import { useWorkspaces } from "../../workspaces/hooks";
import { describeApiError } from "../../common/errors";
import { triggerCsvDownload } from "../../common/csv";
import { useBulkImportAssets } from "../hooks";
import { BULK_IMPORT_ASSETS_MAX_ROWS, type BulkImportAssetsResult } from "../types";
import {
    ASSET_CSV_HEADERS,
    buildAssetCsvTemplate,
    buildFailedRowsCsv,
    correlateFailedImportRows,
    parseAssetsCsv,
    type ParsedAssetRow,
    type ParseAssetsCsvResult,
} from "./parseAssetsCsv";
import { applyUserTokens } from "./applyUserTokens";

/**
 * Bulk-import flow for asset rows. Structurally identical to the
 * workspaces importer (idle → preview → importing → result) — see
 * `BulkImportWorkspacesPage` for the rationale on why this is a
 * dedicated route rather than a modal.
 *
 * The asset-specific wrinkles:
 *
 *   1. Office FK resolution at parse time. We load the sage-offices
 *      list and pass it to `parseAssetsCsv` so unknown/deleted office
 *      ids are caught locally rather than after upload.
 *   2. Within-file serial/tag duplicate detection. The parser flags
 *      the later occurrence with a "duplicates row N" error so the
 *      operator fixes the file before pressing Import.
 *   3. Required `assignmentAction` (Preserve / Assign / Release) plus
 *      optional assignment columns. Assign creates or moves the asset
 *      in the same import; re-uploading the same serial is a
 *      reconciliation (skip / move / release), not a duplicate.
 */
type PageState =
    | { kind: "idle"; fileName: string | null }
    | { kind: "preview"; fileName: string; parse: ParseAssetsCsvResult }
    | { kind: "importing"; fileName: string; parse: ParseAssetsCsvResult }
    | {
          kind: "result";
          fileName: string;
          parse: ParseAssetsCsvResult;
          result: BulkImportAssetsResult;
      };

export function BulkImportAssetsPage(): ReactElement {
    const canEdit = useCanEdit();
    const client = useApiClient();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [state, setState] = useState<PageState>({ kind: "idle", fileName: null });
    const [readError, setReadError] = useState<string | null>(null);

    // Match the workspaces importer's office query exactly so both
    // pages reuse the same cache entry and don't double-fetch.
    const officesQuery = useSageOffices({ q: "", includeDeleted: true });
    const offices = useMemo(() => officesQuery.data ?? [], [officesQuery.data]);

    // Workspace directory for optional assignment-column resolution
    // and the copy-id panel. Include deleted so a stale id in the CSV
    // can be flagged as "deleted" rather than silently unknown.
    const workspacesQuery = useWorkspaces({
        q: "",
        sageOfficeId: "",
        type: "",
        includeDeleted: true,
    });
    const workspaces = useMemo(() => workspacesQuery.data ?? [], [workspacesQuery.data]);

    const importMutation = useBulkImportAssets();

    function pickFile(): void {
        // Browsers suppress the change event when the user re-picks
        // the same file path, so editing the CSV on disk and clicking
        // "Pick another file" would silently keep the prior parse.
        // Resetting the input value first guarantees the next
        // selection always fires onChange, even when the path is
        // unchanged.
        if (fileInputRef.current !== null) fileInputRef.current.value = "";
        fileInputRef.current?.click();
    }

    async function handleFileChosen(file: File): Promise<void> {
        setReadError(null);
        try {
            const text = await file.text();
            const parsed = parseAssetsCsv(text, offices, workspaces);
            const parse = await applyUserTokens(client, parsed);
            if (parse.rows.length > BULK_IMPORT_ASSETS_MAX_ROWS) {
                setReadError(
                    `This file has ${parse.rows.length} rows; the maximum per import is ${BULK_IMPORT_ASSETS_MAX_ROWS}. Split the file and import in batches.`,
                );
                setState({ kind: "idle", fileName: file.name });
                return;
            }
            setState({ kind: "preview", fileName: file.name, parse });
        } catch (e) {
            setReadError(`Could not read file: ${(e as Error).message}`);
            setState({ kind: "idle", fileName: file.name });
        }
    }

    function handleStartImport(): void {
        if (state.kind !== "preview") return;
        if (state.parse.hasErrors) return;
        const items = state.parse.rows.map((r) => r.payload);
        setState({ kind: "importing", fileName: state.fileName, parse: state.parse });
        importMutation.mutate(
            { items },
            {
                onSuccess: (result) => {
                    setState({
                        kind: "result",
                        fileName: state.fileName,
                        parse: state.parse,
                        result,
                    });
                },
                onError: () => {
                    setState({ kind: "preview", fileName: state.fileName, parse: state.parse });
                },
            },
        );
    }

    function handleStartOver(): void {
        setReadError(null);
        setState({ kind: "idle", fileName: null });
        importMutation.reset();
        if (fileInputRef.current !== null) fileInputRef.current.value = "";
    }

    function handleDownloadTemplate(): void {
        triggerCsvDownload("assets-template.csv", buildAssetCsvTemplate(offices, workspaces));
    }

    function handleDownloadFailedRows(): void {
        if (state.kind !== "result") return;
        const failed = correlateFailedImportRows(state.parse.rows, state.result.results);
        if (failed.length === 0) return;
        triggerCsvDownload("assets-failed.csv", buildFailedRowsCsv(failed));
    }

    if (!canEdit) {
        // Bulk import is a write-only flow; viewers landing here via a
        // bookmarked URL get bounced back to the Assets list, where the
        // global view-only banner explains the missing role.
        return <Navigate to="/admin/assets" replace />;
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={<ArrowLeft size={14} />}
                            onClick={() => navigate("/admin/assets")}
                        >
                            Assets
                        </Button>
                        <Text role="heading">Bulk import</Text>
                    </Stack>
                }
                trailing={
                    <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Download size={14} />}
                        onClick={handleDownloadTemplate}
                    >
                        Download template
                    </Button>
                }
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 1100 }}>
                    {state.kind === "idle" ? (
                        <>
                            <IdleStep
                                fileName={state.fileName}
                                directoriesPending={
                                    officesQuery.isPending || workspacesQuery.isPending
                                }
                                onPickFile={pickFile}
                                error={readError}
                            />
                            <AvailableOfficesPanel offices={offices} />
                            <AvailableWorkspacesPanel workspaces={workspaces} />
                        </>
                    ) : null}

                    {state.kind === "preview" ? (
                        <>
                            <PreviewStep
                                fileName={state.fileName}
                                parse={state.parse}
                                isImporting={false}
                                importError={
                                    importMutation.isError
                                        ? describeApiError(importMutation.error)
                                        : null
                                }
                                onStartOver={handleStartOver}
                                onPickAnother={pickFile}
                                onImport={handleStartImport}
                            />
                            <AvailableOfficesPanel offices={offices} />
                            <AvailableWorkspacesPanel workspaces={workspaces} />
                        </>
                    ) : null}

                    {state.kind === "importing" ? (
                        <PreviewStep
                            fileName={state.fileName}
                            parse={state.parse}
                            isImporting={true}
                            importError={null}
                            onStartOver={handleStartOver}
                            onPickAnother={pickFile}
                            onImport={handleStartImport}
                        />
                    ) : null}

                    {state.kind === "result" ? (
                        <ResultStep
                            result={state.result}
                            parse={state.parse}
                            onStartOver={handleStartOver}
                            onDownloadFailed={handleDownloadFailedRows}
                            onBackToAssets={() => navigate("/admin/assets")}
                        />
                    ) : null}
                </Stack>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file !== undefined) {
                        void handleFileChosen(file);
                    }
                }}
            />
        </Stack>
    );
}

interface IdleStepProps {
    readonly fileName: string | null;
    readonly directoriesPending: boolean;
    readonly onPickFile: () => void;
    readonly error: string | null;
}

function IdleStep({
    fileName,
    directoriesPending,
    onPickFile,
    error,
}: IdleStepProps): ReactElement {
    return (
        <Surface tone="raised" padding={5}>
            <Stack gap={4}>
                <Stack gap={1}>
                    <Text role="heading">Upload an assets CSV</Text>
                    <Text role="muted">
                        Header row required. Columns: <code>{ASSET_CSV_HEADERS.join(",")}</code>.{" "}
                        <code>assignmentAction</code> is required: <code>Preserve</code> (leave
                        current assignment / create unassigned), <code>Assign</code> (create or
                        move to a workspace or user), or <code>Release</code> (leave unassigned).{" "}
                        <code>assetId</code> is optional — serial number is the identity on
                        re-upload. <code>assetTag</code>, <code>purchaseDate</code> (YYYY-MM-DD),
                        and <code>purchasePrice</code> are optional. Up to{" "}
                        {BULK_IMPORT_ASSETS_MAX_ROWS.toLocaleString()} rows per import.
                    </Text>
                </Stack>

                <Stack direction="row" gap={2} align="center" wrap>
                    <Button
                        variant="primary"
                        leadingIcon={<FileUp size={16} />}
                        onClick={onPickFile}
                        disabled={directoriesPending}
                    >
                        {directoriesPending ? "Loading directories…" : "Choose CSV file"}
                    </Button>
                    {fileName !== null ? <Text role="muted">Last picked: {fileName}</Text> : null}
                </Stack>

                {error !== null ? (
                    <Surface tone="inset" padding={3}>
                        <Text role="muted" color={palette.danger}>
                            {error}
                        </Text>
                    </Surface>
                ) : null}

                <Stack gap={1}>
                    <Text role="eyebrow">How this works</Text>
                    <Text role="muted">
                        Each row is reconciled independently. Re-running the same file is safe —
                        existing assets (matched by <code>assetId</code> or serial number) come
                        back as <em>skipped</em> when the assignment is unchanged, or{" "}
                        <em>updated</em> when Assign moves them or Release unassigns them. They
                        are never created twice. Catalog fields on an existing asset (make, model,
                        office) are left as-is — this import reconciles identity and assignment.
                        For a first load onto desks, set{" "}
                        <code>assignmentAction</code> to <code>Assign</code> and put a workspace
                        name, number, or id in <code>assignedToWorkspaceId</code> (or a user's
                        name, email, or id in <code>assignedToUserId</code> — not both).
                    </Text>
                </Stack>
            </Stack>
        </Surface>
    );
}

function AssignmentPreview({ row }: { readonly row: ParsedAssetRow }): ReactElement {
    const action = row.payload.assignmentAction || "—";
    const workspaceId = row.payload.assignedToWorkspaceId;
    const userId = row.payload.assignedToUserId;
    if (action.toLowerCase() === "assign" && workspaceId !== null) {
        return (
            <Stack gap={0}>
                <Text role="body">Assign · Workspace</Text>
                <Text role="muted">{row.workspaceName ?? workspaceId}</Text>
            </Stack>
        );
    }
    if (action.toLowerCase() === "assign" && userId !== null) {
        return (
            <Stack gap={0}>
                <Text role="body">Assign · User</Text>
                <Text role="muted">{userId}</Text>
            </Stack>
        );
    }
    return <Text role="body">{action}</Text>;
}

interface PreviewStepProps {
    readonly fileName: string;
    readonly parse: ParseAssetsCsvResult;
    readonly isImporting: boolean;
    readonly importError: string | null;
    readonly onStartOver: () => void;
    readonly onPickAnother: () => void;
    readonly onImport: () => void;
}

function PreviewStep({
    fileName,
    parse,
    isImporting,
    importError,
    onStartOver,
    onPickAnother,
    onImport,
}: PreviewStepProps): ReactElement {
    if (parse.fatalError !== null) {
        return (
            <Surface tone="raised" padding={5}>
                <Stack gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">Couldn't parse {fileName}</Text>
                        <Text role="muted" color={palette.danger}>
                            {parse.fatalError}
                        </Text>
                    </Stack>
                    <Stack direction="row" gap={2}>
                        <Button variant="primary" onClick={onPickAnother}>
                            Pick a different file
                        </Button>
                        <Button variant="ghost" onClick={onStartOver}>
                            Start over
                        </Button>
                    </Stack>
                </Stack>
            </Surface>
        );
    }

    const errorRowCount = parse.rows.filter((r) => r.errors.length > 0).length;
    const okRowCount = parse.rows.length - errorRowCount;

    const columns: readonly DataColumn<ParsedAssetRow>[] = [
        {
            id: "row",
            header: "Row",
            width: "60px",
            render: (r) => <Text role="muted">{r.rowIndex}</Text>,
        },
        {
            id: "asset",
            header: "Asset",
            render: (r) => (
                <Stack gap={0}>
                    <Text role="body" weight="semibold">
                        {r.payload.make || <em>(missing)</em>}{" "}
                        {r.payload.model || <em>(missing)</em>}
                    </Text>
                </Stack>
            ),
        },
        {
            id: "type",
            header: "Type",
            width: "100px",
            render: (r) => <Text role="body">{r.payload.type || "—"}</Text>,
        },
        {
            id: "office",
            header: "Sage office",
            render: (r) =>
                r.sageOfficeName !== null ? (
                    <Stack gap={0}>
                        <Text role="body">{r.sageOfficeName}</Text>
                        <Text role="muted">{r.payload.sageOfficeId}</Text>
                    </Stack>
                ) : (
                    <Text role="muted">{r.payload.sageOfficeId || "—"}</Text>
                ),
        },
        {
            id: "serial",
            header: "Serial",
            render: (r) => <Text role="body">{r.payload.serialNumber || <em>(missing)</em>}</Text>,
        },
        {
            id: "tag",
            header: "Tag",
            render: (r) => <Text role="body">{r.payload.assetTag ?? "—"}</Text>,
        },
        {
            id: "assignment",
            header: "Action",
            render: (r) => <AssignmentPreview row={r} />,
        },
        {
            id: "status",
            header: "Status",
            render: (r) =>
                r.errors.length === 0 ? (
                    <Badge tone="ok">Ready</Badge>
                ) : (
                    <Stack gap={1}>
                        <Badge tone="danger">
                            {r.errors.length} issue{r.errors.length === 1 ? "" : "s"}
                        </Badge>
                        {r.errors.map((msg, i) => (
                            <Text key={i} role="muted" color={palette.danger}>
                                {msg}
                            </Text>
                        ))}
                    </Stack>
                ),
        },
    ];

    return (
        <Stack gap={3}>
            <Surface tone="raised" padding={4}>
                <Stack direction="row" align="center" justify="space-between" wrap gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">Preview · {fileName}</Text>
                        <Text role="muted">
                            {parse.rows.length} row{parse.rows.length === 1 ? "" : "s"} parsed ·{" "}
                            <strong>{okRowCount}</strong> ready
                            {errorRowCount > 0 ? (
                                <>
                                    {" · "}
                                    <span style={{ color: palette.danger }}>
                                        <strong>{errorRowCount}</strong> with issues
                                    </span>
                                </>
                            ) : null}
                        </Text>
                    </Stack>
                    <Stack direction="row" gap={2} wrap>
                        <Button variant="ghost" onClick={onPickAnother} disabled={isImporting}>
                            Pick another file
                        </Button>
                        <Button
                            variant="primary"
                            leadingIcon={<Upload size={16} />}
                            onClick={onImport}
                            disabled={isImporting || parse.hasErrors || parse.rows.length === 0}
                        >
                            {isImporting
                                ? `Importing ${parse.rows.length} row${parse.rows.length === 1 ? "" : "s"}…`
                                : `Import ${okRowCount} row${okRowCount === 1 ? "" : "s"}`}
                        </Button>
                    </Stack>
                </Stack>
            </Surface>

            {importError !== null ? (
                <Surface tone="raised" padding={3}>
                    <Text role="muted" color={palette.danger}>
                        Import failed: {importError}
                    </Text>
                </Surface>
            ) : null}

            {parse.hasErrors ? (
                <Surface tone="inset" padding={3}>
                    <Text role="muted" color={palette.danger}>
                        Fix the highlighted rows in your CSV and re-upload. Import is disabled until
                        every row is ready.
                    </Text>
                </Surface>
            ) : null}

            <Surface tone="raised" padding={0}>
                <DataTable<ParsedAssetRow>
                    columns={columns}
                    rows={parse.rows}
                    rowKey={(r) => String(r.rowIndex)}
                />
            </Surface>
        </Stack>
    );
}

interface ResultStepProps {
    readonly result: BulkImportAssetsResult;
    readonly parse: ParseAssetsCsvResult;
    readonly onStartOver: () => void;
    readonly onDownloadFailed: () => void;
    readonly onBackToAssets: () => void;
}

function ResultStep({
    result,
    parse,
    onStartOver,
    onDownloadFailed,
    onBackToAssets,
}: ResultStepProps): ReactElement {
    const failedRows = useMemo(
        () => correlateFailedImportRows(parse.rows, result.results),
        [parse.rows, result.results],
    );

    const allClean = result.failed === 0;

    const failedColumns: readonly DataColumn<{
        readonly row: ParsedAssetRow;
        readonly errorMessage: string;
    }>[] = [
        {
            id: "row",
            header: "Row",
            width: "60px",
            render: (r) => <Text role="muted">{r.row.rowIndex}</Text>,
        },
        {
            id: "asset",
            header: "Asset",
            render: (r) => (
                <Text role="body">
                    {r.row.payload.make || "—"} {r.row.payload.model || "—"}
                </Text>
            ),
        },
        {
            id: "serial",
            header: "Serial",
            render: (r) => <Text role="body">{r.row.payload.serialNumber || "—"}</Text>,
        },
        {
            id: "office",
            header: "Sage office",
            render: (r) => <Text role="body">{r.row.payload.sageOfficeId || "—"}</Text>,
        },
        {
            id: "error",
            header: "Error",
            render: (r) => (
                <Text role="muted" color={palette.danger}>
                    {r.errorMessage}
                </Text>
            ),
        },
    ];

    return (
        <Stack gap={3}>
            <Surface tone="raised" padding={4}>
                <Stack gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">
                            {allClean ? "Import complete" : "Import finished with issues"}
                        </Text>
                        <Text role="muted">
                            <strong>{result.created}</strong> created
                            {result.updated > 0 ? (
                                <>
                                    {" · "}
                                    <strong>{result.updated}</strong> updated
                                </>
                            ) : null}
                            {result.skipped > 0 ? (
                                <>
                                    {" · "}
                                    <strong>{result.skipped}</strong> skipped (no change)
                                </>
                            ) : null}
                            {result.failed > 0 ? (
                                <>
                                    {" · "}
                                    <span style={{ color: palette.danger }}>
                                        <strong>{result.failed}</strong> failed
                                    </span>
                                </>
                            ) : null}
                            {" · "}
                            <strong>{result.totalRows}</strong> total
                        </Text>
                    </Stack>

                    <Stack direction="row" gap={2} wrap>
                        <Button variant="primary" onClick={onBackToAssets}>
                            Back to assets
                        </Button>
                        <Button variant="ghost" onClick={onStartOver}>
                            Import another file
                        </Button>
                        {result.failed > 0 ? (
                            <Button
                                variant="secondary"
                                leadingIcon={<Download size={14} />}
                                onClick={onDownloadFailed}
                            >
                                Download failed rows
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>
            </Surface>

            {failedRows.length > 0 ? (
                <Surface tone="raised" padding={0}>
                    <DataTable<{ readonly row: ParsedAssetRow; readonly errorMessage: string }>
                        columns={failedColumns}
                        rows={failedRows}
                        rowKey={(r) => String(r.row.rowIndex)}
                    />
                </Surface>
            ) : null}
        </Stack>
    );
}
